import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { BarsIcon, TimesIcon, ByteForgeLogo } from "./IconComponents";
import { useTranslation } from "react-i18next";

const HeaderContainer = styled.header<{ scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: ${({ scrolled }) =>
    scrolled ? "var(--background-color)" : "transparent"};
  box-shadow: ${({ scrolled }) =>
    scrolled ? "0 2px 10px rgba(0, 0, 0, 0.1)" : "none"};
  transition: var(--transition);
  padding: ${({ scrolled }) => (scrolled ? "0.5rem 0" : "1rem 0")};
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (min-width: 769px) {
    justify-content: flex-start;
    gap: 3rem;
  }
`;

const Logo = styled(HashLink)`
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

const NavMenu = styled.nav<{ isOpen: boolean }>`
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: ${({ isOpen }) => (isOpen ? "0" : "-100%")};
    width: 70%;
    height: 100vh;
    background-color: var(--background-color);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    transition: var(--transition);
    box-shadow: ${({ isOpen }) =>
      isOpen ? "-5px 0 15px rgba(0, 0, 0, 0.1)" : "none"};
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const NavItem = styled.li`
  margin: 0 1rem;

  @media (max-width: 768px) {
    margin: 1rem 0;
    width: 100%;
    text-align: center;
  }
`;

const NavLink = styled(HashLink)`
  color: var(--text-color);
  font-weight: 500;
  padding: 0.5rem;
  cursor: pointer;
  position: relative;
  text-decoration: none;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: var(--primary-color);
    transition: var(--transition);
  }

  &:hover,
  &.active {
    color: var(--primary-color);

    &:after {
      width: 100%;
    }
  }

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 4px;
    border-radius: 4px;
  }

  @media (max-width: 768px) {
    display: block;
    padding: 1rem;

    &:after {
      display: none;
    }

    &:hover,
    &.active {
      background-color: var(--primary-color);
      color: var(--light-text-color);
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-color);
  cursor: pointer;
  z-index: 1001;

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 4px;
    border-radius: 4px;
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const { t } = useTranslation();
  const location = useLocation();

  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isHomePage) {
      setActiveSection(null);
      return;
    }

    const sectionIds = ["home", "services", "about", "projects", "contact"];

    const observer = new IntersectionObserver(
      entries => {
        const visibleEntry = entries.find(entry => entry.isIntersecting);

        if (visibleEntry) {
          setActiveSection(visibleEntry.target.id);
        }
      },
      {
        root: null,
        rootMargin: "-100px 0px -55% 0px",
        threshold: 0
      }
    );

    sectionIds.forEach(sectionId => {
      const section = document.getElementById(sectionId);

      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [isHomePage]);

  const toggleMenu = () => {
    setIsMenuOpen(previousValue => !previousValue);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const scrollWithHeaderOffset = (element: HTMLElement) => {
    const headerOffset = 80;

    const position =
      element.getBoundingClientRect().top +
      window.pageYOffset -
      headerOffset;

    window.scrollTo({
      top: position,
      behavior: "smooth"
    });
  };

  return (
    <HeaderContainer scrolled={scrolled}>
      <NavContainer>
        <Logo
          smooth
          to="/#home"
          scroll={scrollWithHeaderOffset}
          onClick={closeMenu}
          aria-label="Zur Startseite"
        >
          <ByteForgeLogo />
        </Logo>

        <MobileMenuButton
          type="button"
          onClick={toggleMenu}
          aria-label={isMenuOpen ? "Menü schließen" : "Menü öffnen"}
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? <TimesIcon /> : <BarsIcon />}
        </MobileMenuButton>

        <NavMenu isOpen={isMenuOpen}>
          <NavList>
            <NavItem>
              <NavLink
                smooth
                to="/#home"
                scroll={scrollWithHeaderOffset}
                onClick={closeMenu}
                className={
                  isHomePage && activeSection === "home" ? "active" : ""
                }
              >
                {t("navigation.home")}
              </NavLink>
            </NavItem>

            <NavItem>
              <NavLink
                smooth
                to="/#services"
                scroll={scrollWithHeaderOffset}
                onClick={closeMenu}
                className={
                  isHomePage && activeSection === "services" ? "active" : ""
                }
              >
                {t("navigation.services")}
              </NavLink>
            </NavItem>

            <NavItem>
              <NavLink
                smooth
                to="/#about"
                scroll={scrollWithHeaderOffset}
                onClick={closeMenu}
                className={
                  isHomePage && activeSection === "about" ? "active" : ""
                }
              >
                {t("navigation.about")}
              </NavLink>
            </NavItem>

            <NavItem>
              <NavLink
                smooth
                to="/#projects"
                scroll={scrollWithHeaderOffset}
                onClick={closeMenu}
                className={
                  isHomePage && activeSection === "projects" ? "active" : ""
                }
              >
                {t("navigation.projects")}
              </NavLink>
            </NavItem>

            <NavItem>
              <NavLink
                smooth
                to="/#contact"
                scroll={scrollWithHeaderOffset}
                onClick={closeMenu}
                className={
                  isHomePage && activeSection === "contact" ? "active" : ""
                }
              >
                {t("navigation.contact")}
              </NavLink>
            </NavItem>
          </NavList>
        </NavMenu>
      </NavContainer>
    </HeaderContainer>
  );
};

export default Header;